import React, { useEffect, useRef, useState } from 'react';
import Vapi from "@vapi-ai/web";
import { Mic, MicOff, PhoneOff, Phone } from 'lucide-react';
import axios from 'axios';

export default function VoiceCallComponent() {
  const [isVapiReady, setIsVapiReady] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTranscript, setActiveTranscript] = useState({ text: '', role: '' });
  
  const vapiRef = useRef(null);

  const startCall = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch Config from our Backend
      // Adjust URL if backend is on a different domain/port
      const response = await axios.post('http://localhost:4500/api/call/initiate', {
        userId: 'test_user_' + Math.floor(Math.random() * 1000)
      });
      
      const data = response.data;
      
      if (!data.success) {
        alert("Failed to start call: " + data.error);
        setIsLoading(false);
        return;
      }

      // 2. Initialize Vapi with Public Key (You should put this in .env)
      // REACT_APP_VAPI_PUBLIC_KEY
      const publicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY || "YOUR_VAPI_PUBLIC_KEY";
      if(publicKey === "YOUR_VAPI_PUBLIC_KEY") {
        console.warn("Vapi Public Key is not set in .env! Using placeholder, connection might fail.");
      }
      
      const vapi = new Vapi(publicKey);
      vapiRef.current = vapi;

      // 3. Listen to Vapi Events
      vapi.on('call-start', () => {
        setIsVapiReady(true);
        setCallActive(true);
        setIsLoading(false);
      });
      
      vapi.on('call-end', () => {
        setIsVapiReady(false);
        setCallActive(false);
        setIsSpeaking(false);
        setActiveTranscript({ text: '', role: '' });
      });
      
      vapi.on('speech-start', () => setIsSpeaking(true));
      vapi.on('speech-end', () => setIsSpeaking(false));
      vapi.on('message', (msg) => {
        if (msg.type === 'transcript' && msg.transcript) {
          setActiveTranscript({ text: msg.transcript, role: msg.role });
        }
      });
      vapi.on('error', (e) => {
        console.error("Vapi error:", e);
        setIsLoading(false);
      });

      // 4. Start the Call passing the Backend Config
      vapi.start(data.config);

    } catch (error) {
      console.error("Error starting call:", error);
      alert("Failed to start the call. Make sure the backend is running.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (vapiRef.current) {
        vapiRef.current.stop();
      }
    };
  }, []);

  const handleMute = () => {
    setIsMuted(!isMuted);
    vapiRef.current?.setMuted(!isMuted);
  };

  const handleEndCall = () => {
    vapiRef.current?.stop();
    setIsVapiReady(false);
    setCallActive(false);
    setIsSpeaking(false);
    setActiveTranscript({ text: '', role: '' });
  };

  return (
    <div className="flex flex-col items-center justify-center h-[70vh] bg-transparent text-white w-full rounded-2xl border border-gray-700/50 backdrop-blur-sm relative overflow-hidden p-8 mt-10">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-purple-900/20 z-0"></div>
      
      <div className="z-10 flex flex-col items-center">
        <h2 className="text-3xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          {isLoading ? "Connecting to AI..." : isVapiReady ? "AI Assistant Active" : "Need Help? Talk to our AI"}
        </h2>

        {/* Cool pulse animation when AI or User is speaking */}
        {callActive ? (
          <div className="relative flex justify-center items-center mb-12 mt-4">
             {/* Glow behind */}
            <div className={`absolute w-32 h-32 rounded-full transition-all duration-300 ${isSpeaking ? 'bg-blue-500/40 scale-150 animate-pulse' : 'bg-blue-900/20 scale-100'}`} />
            <div className={`absolute w-32 h-32 rounded-full transition-all duration-300 ${isSpeaking ? 'bg-purple-500/30 scale-125 animate-ping' : 'bg-purple-900/10 scale-100'}`} />
            
            {/* Center Core */}
            <div className={`relative z-10 w-32 h-32 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${isSpeaking ? 'bg-gradient-to-r from-blue-500 to-purple-500 shadow-[0_0_40px_rgba(59,130,246,0.6)] scale-110' : 'bg-gray-800 border-2 border-blue-500/30 scale-100'}`}>
              <Mic className={`w-12 h-12 ${isSpeaking ? 'text-white' : 'text-gray-400'}`} />
            </div>
          </div>
        ) : (
          <div className="w-32 h-32 rounded-full mb-12 mt-4 bg-gray-800/50 flex items-center justify-center border-2 border-gray-700">
             <Phone className="w-12 h-12 text-gray-500" />
          </div>
        )}

        {/* Live Captions Box */}
        <div className="h-20 w-full max-w-md flex items-center justify-center mt-2 mb-6">
          {callActive && activeTranscript.text ? (
            <div className={`w-full p-4 rounded-xl text-center shadow-inner ${
              activeTranscript.role === 'user' 
                ? 'bg-blue-900/30 border border-blue-500/30 text-blue-100' 
                : 'bg-purple-900/30 border border-purple-500/30 text-purple-100'
            }`}>
              <span className="opacity-60 text-xs uppercase tracking-wider block mb-1 font-semibold">
                {activeTranscript.role === 'user' ? 'You' : 'AI Assistant'}
              </span>
              <p className="italic text-lg">"{activeTranscript.text}"</p>
            </div>
          ) : callActive ? (
            <div className="text-gray-500 italic">Listening...</div>
          ) : null}
        </div>

        {/* Controls */}
        <div className="flex gap-6 mt-4">
          {!callActive ? (
             <button 
                onClick={startCall}
                disabled={isLoading}
                className="px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 flex items-center gap-3 font-semibold text-lg shadow-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all disabled:opacity-50"
             >
                <Phone className="w-5 h-5" />
                {isLoading ? "Connecting..." : "Start Call"}
             </button>
          ) : (
            <>
              <button 
                onClick={handleMute} 
                className={`p-5 rounded-full transition-colors ${isMuted ? 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30' : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'}`}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
              
              <button 
                onClick={handleEndCall} 
                className="p-5 rounded-full bg-red-600 hover:bg-red-500 text-white shadow-lg hover:shadow-[0_0_20px_rgba(220,38,38,0.5)] transition-all"
                title="End Call"
              >
                <PhoneOff className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
